import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import { batch } from "~/tools/impulse";

import styles from "./_highlight-timestamp-cursor.module.css";
import { useTimestampCursor, useTimestampCursorState } from "./use-timestamp-cursor";

const DEFAULT_ELEMENT = "div";

interface HighlightTimestampCursorOwnProps {
	timestamp: Time;
	shouldHighlightMarker?: boolean;
	shouldSetCursor?: boolean;
	shouldToggleMarker?: boolean;
	children?: React.ReactNode;
	className?: string;
}

export type HighlightTimestampCursorElement = React.ElementType<{
	onClick: undefined | React.MouseEventHandler;
	onMouseEnter: undefined | React.MouseEventHandler;
	onMouseLeave: undefined | React.MouseEventHandler;
}>;

export type HighlightTimestampCursorProps<TElement extends React.ElementType = typeof DEFAULT_ELEMENT> =
	PolymorphicBoxProps<TElement, HighlightTimestampCursorOwnProps>;

export const HighlightTimestampCursor: <TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
	props: HighlightTimestampCursorProps<TElement>,
) => React.ReactNode = React.forwardRef(
	(
		{
			el: Element = DEFAULT_ELEMENT,
			timestamp,
			shouldSetCursor = false,
			shouldHighlightMarker = true,
			shouldToggleMarker = true,
			onClick,
			onMouseEnter,
			onMouseLeave,
			className,
			...props
		}: HighlightTimestampCursorProps<React.ElementType>,
		ref,
	) => {
		const timestampCursor = useTimestampCursor();
		const timestampCursorState = useTimestampCursorState();

		const setCursor = (time: undefined | Time) => {
			if (shouldSetCursor) {
				timestampCursor.setValue(time?.valueOf());
			}
		};

		const hover = (time: undefined | Time) => {
			if (shouldHighlightMarker) {
				timestampCursorState.setHighlightedMarker(time);
			}
		};

		return (
			<Element
				{...props}
				className={cx(styles.highlightTimestampCursor, className)}
				onClick={(event: React.MouseEvent<any>) => {
					batch((scope) => {
						if (shouldToggleMarker && timestampCursorState.isHotkeyPressed(scope)) {
							timestampCursorState.toggleMarker(timestamp);
							timestampCursorState.setHighlightedMarker(timestamp);
							event.preventDefault();
							event.stopPropagation();
						} else {
							onClick?.(event);
						}
					});
				}}
				onMouseEnter={(event: React.MouseEvent<any>) => {
					batch(() => {
						setCursor(timestamp);
						hover(timestamp);
						onMouseEnter?.(event);
					});
				}}
				onMouseLeave={(event: React.MouseEvent<any>) => {
					batch(() => {
						setCursor(undefined);
						hover(undefined);
						onMouseLeave?.(event);
					});
				}}
				ref={ref}
			/>
		);
	},
);
